import { useQuery } from "@tanstack/react-query";
import { User } from "@/types/user";
import { userService } from "@/services/userService";
import { authService } from "@/services/authService";
import { useTenant } from "@/contexts/TenantContext";
import { useAuthStore, FirebaseIdentity } from "@/stores/useAuthStore";

// Re-export the store's action + identity hooks so components have one auth import.
export { useAuthActions, useFirebaseUser } from "@/stores/useAuthStore";

interface ContactProfileResult {
  user: User | null;
  missingContact: boolean;
}

// When a user signs up in another tab, this tab picks up the new Firebase
// identity (via AuthInitializer's cross-tab sync) before the other tab's
// idx-signup has finished writing the contact. Re-validate a few times so a
// contact that is still being created isn't mistaken for a missing one — which
// would trip the auto-logout and sign the user out of every tab.
const CONTACT_LANDING_RETRIES = 2;
const CONTACT_LANDING_RETRY_MS = 1500;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Single, un-debounced resolution of the signed-in user's Spanner web-user
// profile. Retry/caching/dedup are handled by React Query (below), not by
// hand-rolled loops, timeouts, or a localStorage cache. Throws on real errors so
// React Query surfaces + retries them; a genuinely absent web user is not an error.
const resolveContactProfile = async (
  firebaseUser: FirebaseIdentity,
  orgId: string
): Promise<ContactProfileResult> => {
  // Validate the signed-in web user against Spanner (replaces the email-keyed
  // Firestore Contacts query). idx-validate uses the Firebase ID token.
  const { valid, user } = await userService.validateWebUser(firebaseUser.uid, orgId);
  if (valid && user) {
    return { user, missingContact: false };
  }

  // Not a known web user yet. If a registration is in flight, bootstrap the
  // Contact + WebUser via idx-signup (backend runs all enrichment).
  const registrationData = authService.getRegistrationData();
  if (registrationData) {
    const createdUser = await userService.getOrCreateUserProfile({
      id: firebaseUser.uid,
      email: firebaseUser.email || "",
      ...registrationData,
      tenantId: registrationData.tenantId || orgId,
    });
    authService.clearRegistrationData();
    return { user: createdUser, missingContact: false };
  }

  // No local registration, but the contact may be mid-creation in another tab.
  // Re-validate before concluding the identity is orphaned (AuthInitializer
  // auto-logs-out on a genuinely missing contact).
  for (let attempt = 0; attempt < CONTACT_LANDING_RETRIES; attempt++) {
    await delay(CONTACT_LANDING_RETRY_MS);
    const retry = await userService.validateWebUser(firebaseUser.uid, orgId);
    if (retry.valid && retry.user) {
      return { user: retry.user, missingContact: false };
    }
  }

  return { user: null, missingContact: true };
};

/**
 * Resolves + caches the signed-in user's Firestore contact profile. Runs only
 * once identity and tenant are known, and not mid-Google-sign-in.
 */
export const useContactProfile = () => {
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const isInGoogleSignInFlow = useAuthStore((s) => s.isInGoogleSignInFlow);
  const { config } = useTenant();
  const orgId = config?.id;

  return useQuery({
    queryKey: ["contactProfile", firebaseUser?.uid, orgId],
    queryFn: () => resolveContactProfile(firebaseUser!, orgId!),
    enabled: !!firebaseUser && !!orgId && !isInGoogleSignInFlow,
    staleTime: 5 * 60 * 1000,
  });
};

// --- Public selector hooks (backed by store identity + the profile query) ---

export const useAuthUser = (): User | null =>
  useContactProfile().data?.user ?? null;

export const useIsAuthenticated = (): boolean => {
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const { data } = useContactProfile();
  return !!firebaseUser && !!data?.user;
};

export const useAuthLoading = (): boolean => {
  const isInitializing = useAuthStore((s) => s.isInitializing);
  const { isLoading } = useContactProfile();
  return isInitializing || isLoading;
};

export const useHasMissingContact = (): boolean => {
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const { data } = useContactProfile();
  return !!firebaseUser && !!data?.missingContact;
};
